/**
 * Iter 34 — Admin Students (read-only directory / caseload oversight).
 *
 * One sortable, filterable view of every enrolled student across all
 * providers. Same engine the provider-side roster uses, lifted into the
 * cross-provider lens. Future bridge to Reports + reassignment + IEP/MET.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  GraduationCap, UserPlus, UserMinus, Funnel, MagnifyingGlass, ArrowUp, ArrowDown,
  CaretUp, CaretDown, Sparkle, Door, ClockCounterClockwise, Buildings,
  Archive,
  ArrowCounterClockwise,
  Trash,
  DownloadSimple,
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAdminStudents, useAdminStudentsSummary, useAdminProviders, useSchools, invalidateAdminStudents } from "@/lib/adminQueries";
import { RefreshChip } from "@/lib/formatUpdatedAgo";
import { useAuth } from "@/lib/auth";
// FP3-081 → FP3-081b — Family-name sort helper. Applies to the
// Student column so "Adelina Ramos Martinez" sorts under Ramos, not
// Adelina; "Adam Aranda" under Aranda. Matches provider Caseload
// behavior 1:1. Reads structured `last_name` when present, falls
// back to full-name parsing for comma / multi-token shapes.
import { familyNameSortKey, givenNameSortKey } from "@/lib/nameSort";
// FP3-099 — canonical Attendance Pattern labels + normalizer (same
// module Admin Profile / Provider Demographics use) and the shared
// teacher-from-email display fallback (never stored, display-only).
import { attendanceLabel, normalizeAttendancePattern } from "@/lib/studentProfileTaxonomy";
import { deriveTeacherDisplay } from "@/lib/teacherIdentity";
import AdminShell from "./AdminShell";
import AdminAddStudentModal from "@/components/admin/AdminAddStudentModal";
import { Button } from "@/components/ui/button";
import { Plus } from "@phosphor-icons/react";
// FP3-043 Phase C — Persistent Super-Admin "show demo/test data" toggle.
import ShowHiddenDataToggle from "@/components/platform/ShowHiddenDataToggle";
// FP3-043 Phase C4 — Single + bulk archive/exit workflow.
import ArchiveStudentsModal from "@/components/admin/ArchiveStudentsModal";
// FP3-051 — Restore archived student back to active.
import RestoreStudentModal from "@/components/admin/RestoreStudentModal";
// FP3-077 — Bulk service-domain + provider-assignment cleanup tools.
import BulkActionsModal from "@/components/admin/BulkActionsModal";
// FP3-088 — Permanent Delete for students who left before services began.
import PermanentDeleteModal from "@/components/admin/PermanentDeleteModal";

// FP3-099 — Attendance Pattern ascending business order:
// AM → PM → Full day → Partial/variable → Unset (unset always last).
const ATTENDANCE_SORT_RANK = { am: 0, pm: 1, full_day: 2, partial: 3 };

const STATUS_LABELS = {
  active: "IEPs",
  rti: "RTI",
  monitoring: "Monitoring",
  graduated: "Graduated",
  archived: "Archived",
};

const STATUS_COLORS = {
  active: "bg-[#E1EAE4] text-[#1E5A3F]",
  rti: "bg-[#FDF8F3] text-[#9C6B2E]",
  monitoring: "bg-[#EEF1F6] text-[#3F4756]",
  graduated: "bg-[#EDF2F7] text-[#4A6B8C]",
  archived: "bg-[#F3F1EC] text-[#6B7280]",
};

// FP3-043 Phase C4 follow-up — Archive reasons surface inline on
// archived rows so the top-level filter stays a single "Archived"
// chip while the actual reason (graduated / withdrew / parent
// revoked services / moved / no longer eligible / dup-or-test /
// other) is still visible per-row. Mirror of the backend
// `VALID_EXIT_REASONS` enum from `/admin/students/exit-reasons`.
const EXIT_REASON_LABELS = {
  graduated: "Graduated",
  withdrew: "Withdrew",
  parent_revoked_services: "Parent revoked",
  moved_transferred: "Moved / transferred",
  no_longer_eligible: "No longer eligible",
  duplicate_or_test_record: "Duplicate / test",
  other: "Other",
};

// Iter 34 Pass A → FP3-095 — Broad-domain labels + colors moved to the
// shared module so Admin Students and Provider Caseload render
// identical Areas of Impact chips from one source.
import { DOMAIN_LABELS, DOMAIN_COLORS } from "@/lib/impactDomains";

function MetricCard({ label, value, sub, icon: Icon, accent = "default", testid, onClick }) {
  const accents = {
    default: "border-[#E5E3DB] bg-white",
    green: "border-[#1E5A3F]/20 bg-[#E1EAE4]/30",
    amber: "border-[#9C6B2E]/30 bg-[#FDF8F3]",
    slate: "border-[#3F4756]/15 bg-[#3F4756]/5",
    red: "border-[#B85C5C]/25 bg-[#FBEFEC]/60",
  }[accent];
  const iconColor = {
    default: "text-[#6B7280]",
    green: "text-[#1E5A3F]",
    amber: "text-[#9C6B2E]",
    slate: "text-[#3F4756]",
    red: "text-[#B85C5C]",
  }[accent];
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border ${accents} p-4 transition-all hover:shadow-sm ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">{label}</p>
        <Icon size={16} weight="duotone" className={iconColor} />
      </div>
      <p className="text-3xl font-bold text-[#1F2937] mt-2 tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-[#6B7280] mt-1">{sub}</p>}
    </button>
  );
}

function FilterPills({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</span>
      <div className="flex bg-[#F3F1EC] rounded-lg p-0.5 flex-wrap">
        {options.map((opt) => {
          const v = typeof opt === "string" ? opt : opt.value;
          const lbl = typeof opt === "string" ? opt : opt.label;
          const active = value === v;
          return (
            <button
              key={v}
              data-testid={`${testidPrefix}-${v}`}
              onClick={() => onChange(v)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${
                active ? "bg-white text-[#3F4756] shadow-sm" : "text-[#6B7280]"
              }`}
            >
              {v === "all" ? "All" : lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// FP3-035B-1 correction — Multi-select chip filter for Service Domain.
// Empty array = "all". Selecting "All" clears the selection. Selected
// chips render dark; unselected chips are pale.
function MultiFilterPills({ label, options, value = [], onChange, testidPrefix }) {
  const toggle = (v) => {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };
  const clearAll = () => onChange([]);
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</span>
      <div className="flex bg-[#F3F1EC] rounded-lg p-0.5 flex-wrap">
        <button
          data-testid={`${testidPrefix}-all`}
          onClick={clearAll}
          className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${
            value.length === 0 ? "bg-white text-[#3F4756] shadow-sm" : "text-[#6B7280]"
          }`}
        >
          All
        </button>
        {options.map((opt) => {
          const v = typeof opt === "string" ? opt : opt.value;
          const lbl = typeof opt === "string" ? opt : opt.label;
          const active = value.includes(v);
          return (
            <button
              key={v}
              data-testid={`${testidPrefix}-${v}`}
              onClick={() => toggle(v)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                active ? "bg-[#1E3F33] text-white shadow-sm" : "text-[#6B7280] hover:bg-white/60"
              }`}
            >
              {lbl}
            </button>
          );
        })}
        {value.length > 0 && (
          <button
            data-testid={`${testidPrefix}-clear`}
            onClick={clearAll}
            className="px-2 py-1 rounded-md text-[11px] font-medium text-[#9B6753] hover:bg-white/60"
            title="Clear selections"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

// FP3-118 — Collapsible wrapper for filter groups. Presentation only:
// collapsing NEVER mutates the wrapped filter state or triggers a
// re-fetch. Header shows an optional "active-selection summary" so
// the admin doesn't need to expand a collapsed section to remember
// what is filtering the table.
function CollapsibleFilterSection({
  label, summary, defaultOpen = false, testid, children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="w-full border border-[#ECEAE4] rounded-lg bg-white overflow-hidden"
      data-testid={`${testid}-section`}
      data-open={open ? "true" : "false"}
    >
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#FAFAF7] transition-colors"
      >
        <span className="flex items-baseline gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold shrink-0">
            {label}
          </span>
          {summary && (
            <span
              data-testid={`${testid}-summary`}
              className="text-[11.5px] text-[#3F4756] font-medium truncate"
            >
              · {summary}
            </span>
          )}
        </span>
        <CaretDown
          size={12}
          weight="bold"
          className={`shrink-0 text-[#6B7280] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-3 pb-2.5 pt-1 border-t border-[#F1F0EB]">
          {children}
        </div>
      )}
    </div>
  );
}

function initials(name) {
  return (name || "")
    .split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminStudents() {
  const navigate = useNavigate();
  // FP3-053 — Super Admin context awareness. When `?as_school=` is
  // set the page pins itself to that single campus and the chip
  // strip collapses to just "Viewing one campus" so the operator
  // can't accidentally widen the scope back to the whole org.
  const [searchParams] = useSearchParams();
  const asOrg = (searchParams.get("as_org") || "").trim();
  const asSchool = (searchParams.get("as_school") || "").trim();
  const [summary, setSummary] = useState(null);
  // FP3-119B — rows/loading/totalMatching/returnedCount now live in
  // React Query (see `studentsQuery` below). Preserved here as
  // computed values so downstream code (many places below) reads
  // them the same way.
  // FP3-043 Phase A follow-up #8 — Canonical org-scoped schools list
  // powers the Campus filter chips. We must NOT derive chips from
  // distinct `student.campus` strings because legacy rows can carry
  // stale text ("Lincoln Elementary") that doesn't correspond to
  // any school in the admin's org.
  const [schools, setSchools] = useState([]);
  const [schoolsById, setSchoolsById] = useState({});

  // Filters
  const [search, setSearch] = useState("");
  // FP3-053 — Default to "all"; the effect below pins the filter to
  // the school's name once `schoolsById` resolves (we can't resolve
  // id→name synchronously here because schools are fetched async).
  const [campusFilter, setCampusFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [gradeFilter, setGradeFilter] = useState("all");
  // FP3-085 — Old `serviceFilter` (multi-select service-domain chips)
  // retired from the UI. Replaced by `eligibilityFilter` (multi-select
  // Eligibility chips) as the top filter block. `serviceFilter` state
  // removed. The old `Query.domain` param is no longer sent by the FE.
  const [impactFilter, setImpactFilter] = useState([]);
  const [providerFilter, setProviderFilter] = useState("all");
  // FP3-085 — Eligibility is now a MULTI-SELECT chip row; the previous
  // single-select dropdown is removed. Special sentinel "unset" =
  // students missing eligibility on both the legacy and canonical
  // fields.
  const [eligibilityFilter, setEligibilityFilter] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  // Iter 34 — quick chips. "iep_overdue" / "iep_soon" / "met_overdue" /
  // "met_soon" map to server-side iep_status/met_status filters so the
  // table reflects an authoritative count.
  const [quickFilter, setQuickFilter] = useState("none"); // none | added | exited | iep_overdue | iep_soon | met_overdue | met_soon

  // Sort
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  // FP3-035B-1 — Add Student modal state. Refresh the table when a
  // new student is created so admins see immediate feedback.
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // FP3-043 Phase C4 — Bulk select + archive/exit workflow. The
  // checkbox column appears only for roles allowed to archive
  // (super_admin + org_admin/admin). Provider/assistant/evaluator
  // see the table without checkboxes.
  const { user: authUser } = useAuth();
  const canArchive = useMemo(() => {
    const role = (authUser?.permission_role || "").toLowerCase();
    return role === "super_admin" || role === "admin" || role === "org_admin";
  }, [authUser]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveTargetIds, setArchiveTargetIds] = useState([]);
  // FP3-051 — Restore-to-active workflow (single row only this pass).
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState({ id: "", name: "" });

  // FP3-088 — Permanent Delete (pre-service roster hygiene). Distinct
  // from Archive: physically removes the student after a server-side
  // preflight confirms no service history exists.
  const [permaDeleteOpen, setPermaDeleteOpen] = useState(false);
  const [permaDeleteIds, setPermaDeleteIds] = useState([]);
  // FP3-089 — CSV export in-flight flag.
  const [exporting, setExporting] = useState(false);
  // FP3-089 follow-up — server-reported totals. The page loads at
  // most the server row cap; the count bar must show the FULL
  // matching count. FP3-119B — sourced from React Query now.
  const permaCtx = useMemo(() => {
    const p = {};
    if (asOrg) p.as_org = asOrg;
    if (asSchool) p.as_school = asSchool;
    return p;
  }, [asOrg, asSchool]);

  // FP3-077 — Bulk service-domain / provider-assignment cleanup.
  // `bulkKind` is one of the 5 BulkActionsModal flows ("" = closed).
  const [bulkKind, setBulkKind] = useState("");
  const openBulk = (kind) => setBulkKind(kind);
  const closeBulk = () => setBulkKind("");

  // FP3-119B — Pull a cached roster on mount and whenever the
  // super-admin org context changes so the provider picker stays
  // scoped. React Query keeps the payload cached across page
  // navigations (10-min stale window).
  const adminProvidersQuery = useAdminProviders(
    canArchive ? (asOrg ? { organization_id: asOrg } : {}) : undefined,
    { enabled: !!canArchive },
  );
  const adminProviders = adminProvidersQuery.data || [];

  // FP3-119B — Summary tile totals via React Query too, so the
  // small caseload roll-ups don't refetch on every navigation.
  const summaryParams = useMemo(() => {
    const p = {};
    if (asOrg) p.as_org = asOrg;
    if (asSchool) p.as_school = asSchool;
    return p;
  }, [asOrg, asSchool]);
  const summaryQuery = useAdminStudentsSummary(summaryParams);
  useEffect(() => {
    if (summaryQuery.data) setSummary(summaryQuery.data);
  }, [summaryQuery.data]);

  // FP3-119B — Schools list cached too. `/api/schools` is
  // auth-gated AND org-scoped; extra org narrowing keeps the campus
  // chip strip small when a super_admin has an org pinned.
  const schoolsParams = useMemo(
    () => (asOrg ? { organization_id: asOrg } : undefined),
    [asOrg],
  );
  const schoolsQuery = useSchools(schoolsParams);
  useEffect(() => {
    const rows = Array.isArray(schoolsQuery.data) ? schoolsQuery.data : [];
    const cleaned = rows
      .filter((s) => s && s.id && (s.name || "").trim())
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    setSchools(cleaned);
    const idx = {};
    cleaned.forEach((s) => {
      idx[s.id] = s;
    });
    setSchoolsById(idx);
  }, [schoolsQuery.data]);

  // FP3-053 — Re-pin campus filter whenever the URL school context
  // changes (super admin picks a different school from the sidebar
  // selector). The backend matches `params.campus` against
  // `student.campus` (a NAME string, not id), so we look up the
  // school's name from the canonical org-scoped list once it loads.
  // Clearing `?as_school=` falls back to "all" so the chip strip
  // becomes interactive again.
  useEffect(() => {
    if (!asSchool) {
      // School pin cleared — only auto-reset the filter if it was
      // previously driven by the pin (i.e. matches a known school's
      // name in the org-scoped index).
      return;
    }
    // Resolve once `schoolsById` finishes loading; if the lookup
    // misses (stale id), fall back to "all" so the page doesn't
    // 0-filter forever.
    const schoolName = (schoolsById[asSchool] || {}).name || "";
    if (schoolName) {
      setCampusFilter(schoolName);
    } else if (Object.keys(schoolsById).length > 0) {
      // schools loaded but the id wasn't found → safe fallback.
      setCampusFilter("all");
    }
  }, [asSchool, schoolsById]);

  // FP3-089 hotfix / FP3-119B — Stale-response-drop is now handled
  // by React Query itself (each new filter set gets a fresh query
  // key; in-flight requests for previous keys are cancelled).

  // FP3-099 — Teacher + Attendance Pattern sorts run server-side over
  // the FULL filtered result set, so switching to them re-fetches with
  // sort params instead of re-ordering only the loaded page.
  const serverSortBy =
    sortKey === "teacher" || sortKey === "attendance" ? sortKey : "";
  const serverSortDir = serverSortBy ? sortDir : "";

  // FP3-119B — Cache the student list via React Query keyed by the
  // full filter+sort param set. Revisiting the page with unchanged
  // filters renders instantly from cache; changing a filter creates
  // a new key and refetches. React Query cancels stale in-flight
  // requests automatically (no need for the old fetchSeqRef guard).
  const studentQueryParams = {
    ...buildStudentFilterParams(),
    ...(serverSortBy ? { sort_by: serverSortBy, sort_dir: serverSortDir } : {}),
  };
  const studentsQuery = useAdminStudents(studentQueryParams);
  const rows = studentsQuery.data?.students || [];
  const totalMatching =
    typeof studentsQuery.data?.total_matching_count === "number"
      ? studentsQuery.data.total_matching_count
      : rows.length;
  const returnedCount =
    typeof studentsQuery.data?.returned_count === "number"
      ? studentsQuery.data.returned_count
      : rows.length;
  const loading = studentsQuery.isPending;
  const refetching = studentsQuery.isFetching && !studentsQuery.isPending;
  // reloadToken triggers an invalidate/refetch of the current key.
  useEffect(() => {
    if (reloadToken > 0) invalidateAdminStudents();
  }, [reloadToken]);

  // FP3-089 — ONE param builder shared by the table fetch and the CSV
  // export so both always represent the exact same filtered set.
  function buildStudentFilterParams() {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (campusFilter !== "all") params.campus = campusFilter;
    if (gradeFilter !== "all") params.grade = gradeFilter;
    // FP3-085 — Areas of Impact filter (broad-domain projection) —
    // still sent. Service Domain filter param dropped from the UI.
    if (impactFilter && impactFilter.length > 0) params.impact_domain = impactFilter.join(",");
    if (providerFilter !== "all") params.provider_id = providerFilter;
    // FP3-085 — Eligibility is now multi-select, comma-separated on the
    // wire. Empty array = no filter. Legacy sentinel "unset" preserved.
    if (eligibilityFilter && eligibilityFilter.length > 0) {
      params.eligibility = eligibilityFilter.join(",");
    }
    // FP3-043 Phase C4 follow-up — Selecting the "Archived" status
    // chip implicitly includes archived students; otherwise the
    // table would 0-filter (archived rows are server-excluded by
    // default).
    if (includeArchived || statusFilter === "archived") {
      params.include_archived = true;
    }
    if (search.trim()) params.q = search.trim();
    // Iter 34 — server-side IEP/MET status filters
    if (quickFilter === "iep_overdue") params.iep_status = "overdue";
    if (quickFilter === "iep_soon") params.iep_status = "soon";
    if (quickFilter === "met_overdue") params.met_status = "overdue";
    if (quickFilter === "met_soon") params.met_status = "soon";
    // FP3-053 — Pass the super-admin context narrowing params on
    // EVERY call so server-side scoping kicks in (search, IEP/MET
    // quick filters, archived toggle, etc all stay scoped to the
    // selected org/school).
    if (asOrg) params.as_org = asOrg;
    if (asSchool) params.as_school = asSchool;
    return params;
  }

  // FP3-089 — Export the CURRENT filtered + sorted list to CSV via the
  // dedicated uncapped backend endpoint (never just the viewport or
  // the selection).
  const exportCsv = async () => {
    // FP3-089 hotfix — never export while the table is refetching: the
    // list on screen must be the list that gets exported.
    if (loading || exporting) return;
    setExporting(true);
    try {
      // Snapshot the filter + sort state at click time; this exact
      // object is what the server receives.
      const params = {
        ...buildStudentFilterParams(),
        sort_key: sortKey,
        sort_dir: sortDir,
      };
      if (quickFilter === "added" || quickFilter === "exited") {
        params.quick_view = quickFilter;
      }
      // FP3-089 cache hotfix — added AFTER the real filter snapshot: a
      // unique URL per click so no browser/proxy/CDN layer can ever
      // replay a previous export's bytes. Ignored by server filtering.
      params._export_nonce = Date.now();
      const r = await api.adminStudentsExportCsv(params);
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename =
        (m && m[1]) ||
        `Frame-Practice-Students-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(
        new Blob([r.data], { type: "text/csv;charset=utf-8" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const n = Number(r.headers?.["x-student-count"]);
      const count =
        Number.isFinite(n) && n >= 0 ? n : totalMatching ?? visible.length;
      toast.success(`${count} ${count === 1 ? "student" : "students"} exported to CSV.`);
    } catch {
      toast.error("Export failed — no file was downloaded. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const visible = useMemo(() => {
    let out = rows;
    // "added" / "exited" remain client-side because they hinge on
    // computed flags rather than dedicated server filters. The IEP/MET
    // chips are already applied server-side via params.
    if (quickFilter === "added") out = out.filter((r) => r.recently_added);
    if (quickFilter === "exited") out = out.filter((r) => r.recently_exited);
    out = [...out].sort((a, b) => {
      // FP3-099 — Teacher + Attendance Pattern sorts: missing values
      // ALWAYS sort last regardless of direction; family name is the
      // stable secondary tie-breaker. (Server already ordered the
      // full set; this keeps the loaded rows consistent.)
      const familyTie = (x, y) => {
        const fx = familyNameSortKey(x);
        const fy = familyNameSortKey(y);
        if (fx !== fy) return fx < fy ? -1 : 1;
        const gx = givenNameSortKey(x);
        const gy = givenNameSortKey(y);
        if (gx === gy) return 0;
        return gx < gy ? -1 : 1;
      };
      const dirMul = sortDir === "asc" ? 1 : -1;
      if (sortKey === "teacher") {
        const ta = (a.teacher || "").trim().toLowerCase();
        const tb = (b.teacher || "").trim().toLowerCase();
        if (!ta || !tb) {
          if (!ta && !tb) return familyTie(a, b);
          return ta ? -1 : 1;
        }
        if (ta !== tb) return (ta < tb ? -1 : 1) * dirMul;
        return familyTie(a, b);
      }
      if (sortKey === "attendance") {
        const ra = ATTENDANCE_SORT_RANK[normalizeAttendancePattern(a.attendance_pattern)] ?? 99;
        const rb = ATTENDANCE_SORT_RANK[normalizeAttendancePattern(b.attendance_pattern)] ?? 99;
        if (ra === 99 || rb === 99) {
          if (ra === rb) return familyTie(a, b);
          return ra === 99 ? 1 : -1;
        }
        if (ra !== rb) return (ra - rb) * dirMul;
        return familyTie(a, b);
      }
      let av, bv;
      switch (sortKey) {
        case "grade":
          av = a.grade || ""; bv = b.grade || "";
          break;
        case "campus":
          av = a.campus || ""; bv = b.campus || "";
          break;
        case "status":
          av = a.status || ""; bv = b.status || "";
          break;
        case "minutes":
          av = a.weekly_service_minutes || 0; bv = b.weekly_service_minutes || 0;
          break;
        case "added":
          av = a.created_at || ""; bv = b.created_at || "";
          break;
        // Iter 34 — new oversight sort keys. Empty dates sort last in
        // ascending order so the populated rows are seen first.
        case "dob":
          av = a.date_of_birth || "9999-99-99"; bv = b.date_of_birth || "9999-99-99";
          break;
        case "iep":
          av = a.iep_due_date || "9999-99-99"; bv = b.iep_due_date || "9999-99-99";
          break;
        case "met":
          av = a.met_due_date || "9999-99-99"; bv = b.met_due_date || "9999-99-99";
          break;
        case "exit":
          av = a.exit_date || ""; bv = b.exit_date || "";
          break;
        case "eligibility":
          av = a.eligibility_label || "zzz"; bv = b.eligibility_label || "zzz";
          break;
        case "sex":
          // Empty/not-specified sort last so the filled rows surface.
          av = a.sex && a.sex !== "not_specified" ? a.sex : "zzz";
          bv = b.sex && b.sex !== "not_specified" ? b.sex : "zzz";
          break;
        case "lre":
          // FP3-035B-1 follow-up — Unset rows sort last so set
          // values surface; otherwise alphabetical on the canonical
          // key (A..J).
          av = a.lre || "zzz"; bv = b.lre || "zzz";
          break;
        default:
          // FP3-081 → FP3-081b — Student column now uses the
          // family-name sort helper. "Adelina Ramos Martinez" sorts
          // under Ramos (first family-name token). "Adam Aranda"
          // under Aranda. Matches provider Caseload FP3-081 1:1.
          // Given-name key acts as the tie-break so two Smiths land
          // Bob-before-John. Handles structured last_name AND full-
          // name-only + comma shapes ("Ramos Martinez, Adelina").
          av = familyNameSortKey(a);
          bv = familyNameSortKey(b);
          if (av === bv) {
            const ga = givenNameSortKey(a);
            const gb = givenNameSortKey(b);
            av = ga; bv = gb;
          }
      }
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * (sortDir === "asc" ? 1 : -1);
    });
    return out;
  }, [rows, quickFilter, sortKey, sortDir]);
  // FP3-089 follow-up — the prominent count shows the FULL matching
  // count from the server (rows may be capped). Client-flag quick
  // views (added/exited) filter locally, so they use the loaded rows.
  const displayCount =
    quickFilter === "added" || quickFilter === "exited"
      ? visible.length
      : totalMatching ?? visible.length;

  // FP3-087 — active-filter count for the result-summary bar. Empty
  // arrays are NOT filters; quick views and search count.
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if ((search || "").trim()) n += 1;
    if (campusFilter !== "all") n += 1;
    if (statusFilter !== "all") n += 1;
    if (gradeFilter !== "all") n += 1;
    if (providerFilter !== "all") n += 1;
    if (Array.isArray(eligibilityFilter) && eligibilityFilter.length > 0) n += 1;
    if (Array.isArray(impactFilter) && impactFilter.length > 0) n += 1;
    if (quickFilter !== "none") n += 1;
    return n;
  }, [search, campusFilter, statusFilter, gradeFilter, providerFilter, eligibilityFilter, impactFilter, quickFilter]);

  // FP3-087 — friendly sort description shown next to the count.
  const sortDescription = useMemo(() => {
    const asc = sortDir === "asc";
    switch (sortKey) {
      case "grade": return asc ? "Grade, low to high" : "Grade, high to low";
      case "dob": return asc ? "DOB, oldest first" : "DOB, youngest first";
      case "sex": return asc ? "Sex A–Z" : "Sex Z–A";
      case "eligibility": return asc ? "Eligibility A–Z" : "Eligibility Z–A";
      case "lre": return asc ? "LRE A–J" : "LRE J–A";
      case "campus": return asc ? "Campus A–Z" : "Campus Z–A";
      case "teacher": return asc ? "Teacher A–Z" : "Teacher Z–A";
      case "attendance": return asc ? "Attendance AM → Partial" : "Attendance Partial → AM";
      case "minutes": return asc ? "Min/wk, low to high" : "Min/wk, high to low";
      case "iep": return asc ? "IEP Due, earliest first" : "IEP Due, latest first";
      case "met": return asc ? "MET Due, earliest first" : "MET Due, latest first";
      case "status": return asc ? "Status A–Z" : "Status Z–A";
      case "exit": return asc ? "Exit date, oldest first" : "Exit date, newest first";
      case "added": return asc ? "Date added, oldest first" : "Date added, newest first";
      default: return asc ? "Last name A–Z" : "Last name Z–A";
    }
  }, [sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Date-like sorts default desc (most-recent first); IEP/MET asc
      // (next-due first). Minutes desc.
      const ascByDefault = ["iep", "met"];
      const descByDefault = ["minutes", "added", "dob", "exit"];
      if (descByDefault.includes(key)) setSortDir("desc");
      else if (ascByDefault.includes(key)) setSortDir("asc");
      else setSortDir("asc");
    }
  };

  // Filter option lists derived from the current rows + summary
  // FP3-043 Phase A follow-up #8 — Campus filter chips come from
  // canonical org-scoped `/api/schools` ONLY. Distinct
  // `student.campus` strings are intentionally NOT used here — they
  // can carry stale text from before follow-up #7 ("Lincoln
  // Elementary" / "Westfield Elementary") that does NOT correspond
  // to any school in the admin's organization.
  const campuses = useMemo(
    () => ["all", ...schools.map((s) => s.name)],
    [schools],
  );
  // Data-hygiene signal: count of students whose `campus` text does
  // not match any school in the org. We surface this as a hygiene
  // chip below so admins can repair the linkage.
  const stale_campus_count = useMemo(() => {
    if (schools.length === 0) return 0;
    const valid = new Set(schools.map((s) => (s.name || "").toLowerCase()));
    return rows.filter((r) => {
      const c = (r.campus || "").trim().toLowerCase();
      return c && !valid.has(c);
    }).length;
  }, [rows, schools]);
  // FP3-035B-1 correction — Hardcode the full grade list 0-12 so the
  // filter doesn't shrink to whatever grades happen to be enrolled
  // today. Kindergarten = "0" platform-wide. Labels show as "Gr N"
  // for compactness in the filter row.
  const grades = useMemo(() => [
    "all",
    ...Array.from({ length: 13 }, (_, i) => ({ value: String(i), label: `Gr ${i}` })),
  ], []);
  // FP3-035B-1 correction — Domain filter shows EVERY domain in
  // `DOMAIN_LABELS`, not just domains currently in use. Admins can
  // pre-filter for upcoming/uncommon service domains too.
  const serviceAreas = useMemo(
    () => Object.keys(DOMAIN_LABELS).map((k) => ({ value: k, label: DOMAIN_LABELS[k] })),
    [],
  );

  // FP3-085 — Eligibility chip options for the new multi-select filter.
  // Reads the configured Arizona taxonomy from the summary payload
  // (already loaded on mount). Appends the "Unset / Missing" sentinel
  // so admins can find students with no eligibility on either field.
  const eligibilityOptions = useMemo(() => {
    const configured = (summary?.eligibility_categories || []).map((c) => ({
      value: c.key,
      label: c.abbr ? `${c.abbr} · ${c.label}` : c.label,
    }));
    return [...configured, { value: "unset", label: "— Unset / Missing" }];
  }, [summary]);
  const providerOptions = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => (r.assigned_providers || []).forEach((p) => m.set(p.id, p.name)));
    return [{ value: "all", label: "All" }, ...Array.from(m.entries()).map(([id, name]) => ({ value: id, label: name }))];
  }, [rows]);

  return (
    <AdminShell
      title="Students"
      subtitle="Cross-provider student directory · read-only oversight"
      actions={
        <div className="flex items-center gap-2">
          <RefreshChip
            dataUpdatedAt={studentsQuery.dataUpdatedAt}
            isFetching={refetching}
            onRefresh={() => studentsQuery.refetch()}
            testid="students-refresh"
          />
          {/* FP3-043 Phase C — Super-admin only; hidden for org admins. */}
          <ShowHiddenDataToggle
            testid="admin-students-show-hidden-toggle"
            onChange={() => setReloadToken((k) => k + 1)}
          />
          {/* FP3-035B-1 — Manual Add Student (alternative to CSV import). */}
          <Button
            data-testid="admin-add-student-btn"
            onClick={() => setAddModalOpen(true)}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-9 text-xs inline-flex items-center gap-1.5"
          >
            <Plus size={14} weight="bold" /> Add Student
          </Button>
        </div>
      }
    >
      {/* Metric strip */}
      {!summary ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
          <MetricCard
            testid="metric-enrolled"
            label="Enrolled"
            value={summary.enrolled}
            sub={`${summary.total} total in system`}
            icon={GraduationCap}
            accent="slate"
            onClick={() => { setStatusFilter("all"); setIncludeArchived(false); setQuickFilter("none"); }}
          />
          <MetricCard
            testid="metric-active"
            label="IEPs"
            value={summary.by_status.active}
            icon={Sparkle}
            accent="green"
            onClick={() => setStatusFilter("active")}
          />
          <MetricCard
            testid="metric-rti"
            label="RTI / Monitoring"
            value={summary.by_status.rti + summary.by_status.monitoring}
            sub={`${summary.by_status.rti} RTI · ${summary.by_status.monitoring} monitoring`}
            icon={ClockCounterClockwise}
            accent="amber"
          />
          <MetricCard
            testid="metric-recently-added"
            label={`Added · last ${summary.window_days}d`}
            value={summary.recently_added_30d}
            icon={UserPlus}
            accent="default"
            onClick={() => { setQuickFilter("added"); setSortKey("added"); setSortDir("desc"); }}
          />
          <MetricCard
            testid="metric-recently-exited"
            label={`Archived · last ${summary.window_days}d`}
            value={summary.recently_exited_30d}
            sub={`${summary.recently_graduated_30d} graduated · ${summary.recently_archived_30d - summary.recently_graduated_30d} other reasons`}
            icon={UserMinus}
            accent="default"
            onClick={() => { setQuickFilter("exited"); setIncludeArchived(true); }}
          />
        </div>
      )}

      {/* Filter bar */}
      <Card className="border-[#E5E3DB] mb-4">
        <CardContent className="py-3 px-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9B9890]" />
              <Input
                data-testid="student-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, teacher, email…"
                className="pl-8 h-8 text-[12px] w-72"
              />
            </div>
            {/* FP3-053 — When the sidebar context selector has pinned
                a school the campus chip strip is redundant (and
                actively confusing — it can show 100+ "Primary
                Workspace" entries from solo provider workspaces).
                Replace it with a compact read-only pill that
                surfaces the pinned campus + a quick "release"
                affordance via the sidebar selector. */}
            {asSchool ? (
              <div
                data-testid="campus-pinned-by-context"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#E6F1F8] border border-[#1F4F73]/30 text-[11.5px] text-[#0F3554]"
              >
                <span className="text-[9px] uppercase tracking-[0.08em] font-semibold text-[#1F4F73]">
                  Campus
                </span>
                <span className="font-medium truncate max-w-[18ch]">
                  {(schoolsById[asSchool] || {}).name || "Pinned"}
                </span>
                <span className="text-[10px] text-[#1F4F73]/80">
                  (set via sidebar context)
                </span>
              </div>
            ) : (
              <FilterPills
                label="Campus"
                options={campuses}
                value={campusFilter}
                onChange={setCampusFilter}
                testidPrefix="filter-campus"
              />
            )}
            <FilterPills
              label="Status"
              options={[
                "all",
                // FP3-043 Phase C4 follow-up — `archived` is a valid
                // top-level filter now that we treat graduate/withdraw/
                // parent-revoked/etc. as reasons under the broader
                // "Archived" status. Order here drives chip order.
                { value: "active", label: STATUS_LABELS.active },
                { value: "rti", label: STATUS_LABELS.rti },
                { value: "monitoring", label: STATUS_LABELS.monitoring },
                // FP3-093 — Graduated joins the canonical Student
                // Status taxonomy so Admin and Provider filters match.
                { value: "graduated", label: STATUS_LABELS.graduated },
                { value: "archived", label: STATUS_LABELS.archived },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              testidPrefix="filter-status"
            />
          </div>
          <div className="space-y-1.5">
            {/* FP3-118 — Grade / Eligibility / Areas of Impact are the
                three taxonomy-heavy filter groups. All three are
                collapsible and DEFAULT COLLAPSED so the filter panel
                stays compact. Collapse is presentation-only; the
                underlying filter state (and the filtered result set)
                is unchanged when a section is collapsed. */}
            <CollapsibleFilterSection
              label="Grade"
              testid="filter-grade"
              summary={
                gradeFilter !== "all"
                  ? (grades.find((g) => (typeof g === "string" ? g : g.value) === gradeFilter)?.label || `Grade ${gradeFilter}`)
                  : ""
              }
            >
              <FilterPills
                label="Grade"
                options={grades}
                value={gradeFilter}
                onChange={setGradeFilter}
                testidPrefix="filter-grade"
              />
            </CollapsibleFilterSection>
            {/* FP3-085 — ELIGIBILITY replaces the old SERVICE DOMAIN
                filter row. Multi-select ANY-match. "Unset" as a
                sentinel returns students missing eligibility on
                BOTH `eligibility_category` (legacy) and
                `eligibility_categories` (canonical). Reads the
                configured Arizona taxonomy from the summary. */}
            <CollapsibleFilterSection
              label="Eligibility"
              testid="filter-eligibility"
              summary={
                eligibilityFilter.length === 1
                  ? (() => {
                      const opt = eligibilityOptions.find((o) => o.value === eligibilityFilter[0]);
                      // Prefer the short abbreviation before the "·"
                      // separator so the header stays compact ("SLI"
                      // instead of "SLI · Speech/Language Impairment").
                      const raw = opt?.label || eligibilityFilter[0];
                      const short = raw.split(" · ")[0].trim();
                      return short || raw;
                    })()
                  : eligibilityFilter.length > 1
                    ? `${eligibilityFilter.length} selected`
                    : ""
              }
            >
              <MultiFilterPills
                label="Eligibility"
                options={eligibilityOptions}
                value={eligibilityFilter}
                onChange={setEligibilityFilter}
                testidPrefix="filter-eligibility"
              />
            </CollapsibleFilterSection>
            {/* FP3-084b — Areas of Impact filter. Uses the SAME 10 broad
                domain labels as the Student Profile chip row.
                Independent of Eligibility; both filter rows compose
                with AND semantics. */}
            <CollapsibleFilterSection
              label="Areas of impact"
              testid="filter-impact"
              summary={
                impactFilter.length === 1
                  ? (serviceAreas.find((s) => s.value === impactFilter[0])?.label || impactFilter[0])
                  : impactFilter.length > 1
                    ? `${impactFilter.length} selected`
                    : ""
              }
            >
              <MultiFilterPills
                label="Areas of impact"
                options={serviceAreas}
                value={impactFilter}
                onChange={setImpactFilter}
                testidPrefix="filter-impact"
              />
            </CollapsibleFilterSection>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <FilterPills
              label="Provider"
              options={providerOptions}
              value={providerFilter}
              onChange={setProviderFilter}
              testidPrefix="filter-provider"
            />
            {/* FP3-085 — Old single-select Eligibility dropdown removed.
                Eligibility is now a multi-select chip row above the
                Areas of Impact row. */}
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-[#ECEAE4]">
            <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Quick view</span>
            {[
              { value: "none", label: "All" },
              { value: "added", label: `Recently added (${summary?.recently_added_30d || 0})` },
              { value: "exited", label: `Recently exited (${summary?.recently_exited_30d || 0})` },
              { value: "iep_overdue", label: `IEP overdue (${summary?.iep_overdue || 0})`, kind: "danger" },
              { value: "iep_soon", label: `IEP due ≤30d (${summary?.iep_due_30d || 0})`, kind: "warn" },
              { value: "met_overdue", label: `MET overdue (${summary?.met_overdue || 0})`, kind: "danger" },
              { value: "met_soon", label: `MET due ≤30d (${summary?.met_due_30d || 0})`, kind: "warn" },
            ].map((q) => {
              const active = quickFilter === q.value;
              const tone = active
                ? (q.kind === "danger" ? "border-[#B85C5C] bg-[#B85C5C] text-white"
                  : q.kind === "warn" ? "border-[#9C6B2E] bg-[#9C6B2E] text-white"
                  : "border-[#3F4756] bg-[#3F4756] text-white")
                : (q.kind === "danger" ? "border-[#B85C5C]/30 bg-[#FBEFEC]/50 text-[#B85C5C] hover:bg-[#FBEFEC]"
                  : q.kind === "warn" ? "border-[#9C6B2E]/30 bg-[#FDF8F3] text-[#9C6B2E] hover:bg-[#F7EDDF]"
                  : "border-[#E5E3DB] bg-white text-[#6B7280] hover:bg-[#FAFAF7]");
              return (
                <button
                  key={q.value}
                  data-testid={`quick-${q.value}`}
                  onClick={() => {
                    setQuickFilter(q.value);
                    if (q.value === "exited") setIncludeArchived(true);
                    if (q.value === "none") setIncludeArchived(false);
                  }}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium border ${tone}`}
                >
                  {q.label}
                </button>
              );
            })}
            <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-[#6B7280]">
              <input
                data-testid="toggle-include-archived"
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="rounded border-[#E5E3DB]"
              />
              Include archived
            </label>
          </div>
        </CardContent>
      </Card>

      {/* FP3-043 Phase A follow-up #8 — Campus link hygiene strip.
          Surfaces students whose `campus` text doesn't link to any
          school in the org and offers a self-serve repair action. */}
      {stale_campus_count > 0 && (
        <CampusLinkHygiene
          count={stale_campus_count}
          onAfterApply={() => setReloadToken((n) => n + 1)}
        />
      )}

      {/* Data-quality strip — surfaces records that need attention so
          admins can act on incomplete data, not just see counters. */}
      {summary && (summary.missing_dob > 0 || summary.missing_iep > 0 || summary.missing_eligibility > 0) && (
        <div
          data-testid="data-quality-strip"
          className="mb-3 flex items-center gap-2 px-3 py-2 rounded-md border border-[#9C6B2E]/25 bg-[#FDF8F3]/60 text-[11px] text-[#9C6B2E] flex-wrap"
        >
          <Funnel size={12} weight="duotone" />
          <span className="font-medium">Data hygiene:</span>
          {summary.missing_dob > 0 && (
            <span>
              <span className="font-semibold tabular-nums">{summary.missing_dob}</span> missing DOB
            </span>
          )}
          {summary.missing_dob > 0 && (summary.missing_iep > 0 || summary.missing_eligibility > 0) && <span className="text-[#9B9890]">·</span>}
          {summary.missing_iep > 0 && (
            <span>
              <span className="font-semibold tabular-nums">{summary.missing_iep}</span> missing IEP date
            </span>
          )}
          {summary.missing_iep > 0 && summary.missing_eligibility > 0 && <span className="text-[#9B9890]">·</span>}
          {summary.missing_eligibility > 0 && (
            <button
              data-testid="missing-eligibility-link"
              onClick={() => setEligibilityFilter(["unset"])}
              className="hover:underline"
            >
              <span className="font-semibold tabular-nums">{summary.missing_eligibility}</span> missing eligibility
            </button>
          )}
          <span className="text-[10px] text-[#6B7280] ml-auto">Click any count to filter</span>
        </div>
      )}

      {/* FP3-087 — Prominent result-summary bar (count + sort) +
          FP3-043 Phase C4 bulk action bar. aria-live announces count
          changes to screen readers on every filter change. */}
      <div className="flex items-center justify-between mb-2.5 gap-3 flex-wrap">
        <div
          data-testid="results-summary"
          role="status"
          aria-live="polite"
          className="flex items-baseline gap-2 flex-wrap px-3.5 py-2 rounded-lg border border-[#E5E3DB] bg-[#F9F8F6]"
        >
          <span
            data-testid="results-count"
            className="text-[17px] font-semibold text-[#1F2937] tabular-nums leading-none"
          >
            {loading
              ? "…"
              : activeFilterCount > 0
                ? `${displayCount} ${displayCount === 1 ? "student matches" : "students match"}`
                : `${displayCount} ${displayCount === 1 ? "student" : "students"}`}
          </span>
          <span data-testid="results-sort" className="text-[11px] text-[#6B7280]">
            · Sorted by {sortDescription}
          </span>
          {/* FP3-089 follow-up — never silently truncate: when the
              server capped the rows, say so and point at Export CSV. */}
          {totalMatching != null &&
            returnedCount != null &&
            totalMatching > returnedCount && (
              <span
                data-testid="results-truncation-note"
                className="text-[11px] text-[#9C6B2E]"
              >
                · table shows the first {returnedCount} — Export CSV includes
                all {totalMatching}
              </span>
            )}
          {activeFilterCount > 0 && (
            <span data-testid="results-denominator" className="text-[11px] text-[#6B7280]">
              {includeArchived || statusFilter === "archived"
                ? "· includes archived"
                : `· of ${summary?.enrolled ?? "…"} enrolled students`}
            </span>
          )}
          {activeFilterCount === 0 && (includeArchived || statusFilter === "archived") && (
            <span data-testid="results-archived-note" className="text-[11px] text-[#6B7280]">
              · includes archived
            </span>
          )}
          {/* FP3-089 — Export the current filtered list. Admin family
              only; never just the viewport or the selection. */}
          {canArchive && (
            <Button
              data-testid="export-csv-btn"
              size="sm"
              variant="outline"
              disabled={loading || exporting || visible.length === 0}
              title={
                loading
                  ? "Updating list…"
                  : visible.length === 0
                    ? "No students match the current filters."
                    : "Export the current filtered list"
              }
              onClick={exportCsv}
              className="h-7 px-2.5 text-[11.5px] self-center ml-1 border-[#1E3F33]/30 text-[#1E3F33] hover:bg-[#E8F0EB] disabled:opacity-40"
            >
              <DownloadSimple size={12} weight="bold" className="mr-1" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          )}
        </div>
        {canArchive && selectedIds.size > 0 && (
          <div
            data-testid="bulk-action-bar"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FBF3E4] border border-[#E0CFA8]"
          >
            <span
              data-testid="bulk-selected-count"
              className="text-[11.5px] font-semibold text-[#7A3A2C]"
            >
              {selectedIds.size} selected
            </span>
            <Button
              data-testid="bulk-archive-btn"
              size="sm"
              className="h-7 px-2.5 text-[11.5px] bg-[#7A3A2C] hover:bg-[#62301F] text-white"
              onClick={() => {
                setArchiveTargetIds(Array.from(selectedIds));
                setArchiveModalOpen(true);
              }}
            >
              <Archive size={12} weight="duotone" className="mr-1" />
              Archive / Exit selected
            </Button>
            {/* FP3-085 — Bulk actions repurposed from + Domains /
                − Domains (which wrote to `service_areas`) into
                Areas of Impact operations that write to
                `eligibility_categories[primary].subcategories`.
                Same 500-student cap, dry-run + confirm workflow. */}
            <Button
              data-testid="bulk-add-impact-btn"
              size="sm"
              variant="outline"
              className="h-7 px-2.5 text-[11.5px] border-[#1E3F33] text-[#1E3F33] hover:bg-[#E8F0EB]"
              onClick={() => openBulk("add-impact")}
            >
              + Areas of Impact
            </Button>
            <Button
              data-testid="bulk-remove-impact-btn"
              size="sm"
              variant="outline"
              className="h-7 px-2.5 text-[11.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F3F1EC]"
              onClick={() => openBulk("remove-impact")}
            >
              − Areas of Impact
            </Button>
            <Button
              data-testid="bulk-add-provider-btn"
              size="sm"
              variant="outline"
              className="h-7 px-2.5 text-[11.5px] border-[#1E3F33] text-[#1E3F33] hover:bg-[#E8F0EB]"
              onClick={() => openBulk("add-provider")}
            >
              + Provider
            </Button>
            <Button
              data-testid="bulk-remove-provider-btn"
              size="sm"
              variant="outline"
              className="h-7 px-2.5 text-[11.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F3F1EC]"
              onClick={() => openBulk("remove-provider")}
            >
              − Provider
            </Button>
            <Button
              data-testid="bulk-clear-providers-btn"
              size="sm"
              variant="outline"
              className="h-7 px-2.5 text-[11.5px] border-[#E8CFC8] text-[#7A3A2C] hover:bg-[#FBEEEA]"
              onClick={() => openBulk("clear-providers")}
            >
              Clear providers
            </Button>
            {/* FP3-088 — Permanent Delete: visually distinct from
                Archive (deep red, trash icon). Opens the preflight
                modal; blocked students are never deleted. */}
            <Button
              data-testid="bulk-permanent-delete-btn"
              size="sm"
              variant="outline"
              className="h-7 px-2.5 text-[11.5px] border-[#7A1E1E]/40 text-[#7A1E1E] hover:bg-[#FBEEEA]"
              onClick={() => {
                setPermaDeleteIds(Array.from(selectedIds));
                setPermaDeleteOpen(true);
              }}
            >
              <Trash size={12} weight="duotone" className="mr-1" />
              Permanently delete
            </Button>
            <button
              data-testid="bulk-clear-btn"
              onClick={() => setSelectedIds(new Set())}
              className="text-[11px] text-[#6B7280] hover:underline"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <Skeleton className="h-[400px]" />
      ) : visible.length === 0 ? (
        <Card className="border-[#E5E3DB] border-dashed">
          <CardContent className="py-16 text-center">
            <p className="text-sm text-[#6B7280]">No students match the current filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div
          className="border border-[#E5E3DB] rounded-lg overflow-x-auto bg-white"
          data-testid="students-table-scroll"
        >
          {/* FP3-035B-1 — Min-width keeps columns at readable widths
              even with 12 columns; the parent div scrolls horizontally
              on narrow screens. Student column is position: sticky
              so the row's identity stays visible while scrolling. */}
          {/* FP3-087 — Responsive column priority. Priority columns
              (Select / Student / Grade / DOB·Age / Sex / Eligibility /
              IEP Due / MET Due / Campus·Teacher / Providers / Actions)
              stay visible at every desktop width. Areas of Impact
              returns at ≥1536px (2xl); LRE / Min-wk / Status / Exit /
              Added return only at ≥1920px so 1728 stays uncramped.
              Hidden data stays available on the Student Profile. */}
          <table
            className="text-sm w-full min-w-[1150px] 2xl:min-w-[1320px] min-[1920px]:min-w-[1680px]"
            data-testid="students-table"
          >
            <thead className="bg-[#F9F8F6] text-[#6B7280]">
              <tr>
                {canArchive && (
                  <th
                    className="sticky left-0 z-20 bg-[#F9F8F6] px-2 py-2 w-9"
                    data-testid="bulk-select-header"
                  >
                    <Checkbox
                      data-testid="bulk-select-all"
                      checked={
                        // Consider only non-archived rows for the
                        // select-all semantics — archived rows can't
                        // be re-archived so they're excluded.
                        visible.filter((s) => !(s.status === "archived" || s.is_archived)).length > 0 &&
                        visible
                          .filter((s) => !(s.status === "archived" || s.is_archived))
                          .every((s) => selectedIds.has(s.id))
                      }
                      onCheckedChange={(checked) => {
                        const next = new Set(selectedIds);
                        const eligible = visible.filter(
                          (s) => !(s.status === "archived" || s.is_archived)
                        );
                        if (checked) {
                          eligible.forEach((s) => next.add(s.id));
                        } else {
                          eligible.forEach((s) => next.delete(s.id));
                        }
                        setSelectedIds(next);
                      }}
                    />
                  </th>
                )}
                <SortHeader
                  sticky
                  stickyOffset={canArchive ? "left-9" : "left-0"}
                  testid="sort-name"
                  label="Student"
                  active={sortKey === "name"}
                  dir={sortDir}
                  onClick={() => toggleSort("name")}
                />
                <SortHeader testid="sort-grade" label="Grade" active={sortKey === "grade"} dir={sortDir} onClick={() => toggleSort("grade")} />
                <SortHeader testid="sort-dob" label="DOB · Age" active={sortKey === "dob"} dir={sortDir} onClick={() => toggleSort("dob")} />
                {/* FP3-035B correction — Platform-wide Sex column. */}
                <SortHeader testid="sort-sex" label="Sex" active={sortKey === "sex"} dir={sortDir} onClick={() => toggleSort("sex")} />
                <SortHeader testid="sort-eligibility" label="Eligibility" active={sortKey === "eligibility"} dir={sortDir} onClick={() => toggleSort("eligibility")} />
                <SortHeader testid="sort-iep" label="IEP Due" active={sortKey === "iep"} dir={sortDir} onClick={() => toggleSort("iep")} />
                <SortHeader testid="sort-met" label="MET Due" active={sortKey === "met"} dir={sortDir} onClick={() => toggleSort("met")} />
                <SortHeader testid="sort-campus" label="Campus" active={sortKey === "campus"} dir={sortDir} onClick={() => toggleSort("campus")} />
                {/* FP3-099 — Campus and Teacher are separate sortable
                    columns; Attendance Pattern is a first-class
                    sortable column (AM → PM → Full → Partial, unset
                    always last). */}
                <SortHeader testid="sort-teacher" label="Teacher" active={sortKey === "teacher"} dir={sortDir} onClick={() => toggleSort("teacher")} />
                <SortHeader testid="sort-attendance" label="Attendance Pattern" active={sortKey === "attendance"} dir={sortDir} onClick={() => toggleSort("attendance")} />
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-semibold">Providers</th>
                {/* FP3-085 — Service Domains column replaced by
                    Areas of Impact. Renders `impact_domains` (broad-
                    domain projection of eligibility subcategories).
                    Same 10-label chip style. */}
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-semibold hidden 2xl:table-cell" data-testid="col-areas-of-impact">Areas of impact</th>
                {/* FP3-035B-1 follow-up — LRE compact column.
                    Single-letter chip (A/B/.../J); empty cell when unset. */}
                <SortHeader className="hidden min-[1920px]:table-cell" testid="sort-lre" label="LRE" active={sortKey === "lre"} dir={sortDir} onClick={() => toggleSort("lre")} />
                <SortHeader className="hidden min-[1920px]:table-cell" testid="sort-minutes" label="Min/wk" active={sortKey === "minutes"} dir={sortDir} onClick={() => toggleSort("minutes")} align="right" />
                <SortHeader className="hidden min-[1920px]:table-cell" testid="sort-status" label="Status" active={sortKey === "status"} dir={sortDir} onClick={() => toggleSort("status")} />
                <SortHeader className="hidden min-[1920px]:table-cell" testid="sort-exit" label="Exit" active={sortKey === "exit"} dir={sortDir} onClick={() => toggleSort("exit")} />
                <SortHeader className="hidden min-[1920px]:table-cell" testid="sort-added" label="Added" active={sortKey === "added"} dir={sortDir} onClick={() => toggleSort("added")} />
                {canArchive && (
                  <th
                    className="sticky right-0 z-20 bg-[#F9F8F6] px-3 py-2 text-right text-[10px] uppercase tracking-wider font-semibold shadow-[-2px_0_2px_-1px_rgba(0,0,0,0.04)]"
                    data-testid="actions-header"
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`student-row-${s.id}`}
                  className="border-t border-[#ECEAE4] hover:bg-[#FAFAF7] cursor-pointer group"
                  onClick={() => navigate(`/admin/students/${s.id}`)}
                >
                  {canArchive && (
                    <td
                      className="sticky left-0 z-10 bg-white group-hover:bg-[#FAFAF7] px-2 py-2.5 w-9"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        data-testid={`student-select-${s.id}`}
                        disabled={s.status === "archived" || s.is_archived}
                        checked={selectedIds.has(s.id)}
                        onCheckedChange={(checked) => {
                          if (s.status === "archived" || s.is_archived) return;
                          const next = new Set(selectedIds);
                          if (checked) next.add(s.id);
                          else next.delete(s.id);
                          setSelectedIds(next);
                        }}
                      />
                    </td>
                  )}
                  <td
                    className={`px-2 py-2.5 sticky ${
                      canArchive ? "left-9" : "left-0"
                    } z-10 bg-white group-hover:bg-[#FAFAF7] shadow-[2px_0_2px_-1px_rgba(0,0,0,0.04)]`}
                  >
                    <div className="flex items-center gap-2 min-w-0 overflow-hidden max-w-[210px] 2xl:max-w-[300px]">
                      <Avatar className="w-7 h-7 bg-[#E8F0EB] shrink-0">
                        <AvatarFallback className="text-[10px] font-semibold text-[#1E3F33]">{initials(s.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-[#1F2937] truncate flex items-center gap-1">
                          {s.name}
                          {s.recently_added && (
                            <Badge className="bg-[#E8F0EB] text-[#1E3F33] hover:bg-[#E8F0EB] text-[9px] font-semibold" title="Added in the last 30 days">
                              new
                            </Badge>
                          )}
                          {s.recently_exited && (
                            <Badge className="bg-[#F3F1EC] text-[#6B7280] hover:bg-[#F3F1EC] text-[9px]" title="Exited in the last 30 days">
                              exited
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {s.readable_id && (
                            <span data-testid={`readable-id-${s.id}`} className="text-[9px] font-mono text-[#9B9890] tabular-nums">{s.readable_id}</span>
                          )}
                          {/* FP3-045 — Surface External Student ID next
                              to the platform readable_id so admins can
                              recognize school-records IDs at a glance. */}
                          {s.external_id && (
                            <span
                              data-testid={`external-id-${s.id}`}
                              title="External Student ID"
                              className="text-[9px] font-mono text-[#6B7280] tabular-nums px-1 rounded bg-[#F3F1EC]"
                            >
                              {s.external_id}
                            </span>
                          )}
                          {s.teacher_email && <p className="text-[10px] text-[#9B9890] truncate">· {s.teacher_email}</p>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-[12px] text-[#1F2937] tabular-nums">{s.grade || "—"}</td>
                  <td className="px-2 py-2.5 tabular-nums">
                    {s.date_of_birth ? (
                      <>
                        <p className="text-[12px] text-[#1F2937]">{fmtDate(s.date_of_birth)}</p>
                        {s.age != null && <p className="text-[10px] text-[#6B7280]">age {s.age}</p>}
                      </>
                    ) : (
                      <span className="text-[10px] text-[#B85C5C] italic" title="DOB missing">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-[12px] text-[#1F2937]" data-testid={`sex-${s.id}`}>
                    {s.sex
                      ? s.sex === "not_specified"
                        ? <span className="text-[10px] text-[#9B9890] italic">not specified</span>
                        : s.sex[0].toUpperCase() + s.sex.slice(1)
                      : <span className="text-[10px] text-[#9B9890] italic" title="Sex not set">—</span>}
                  </td>
                  <td className="px-2 py-2.5">
                    {/* FP3-035B-1 correction — Compact display for
                        multi-eligibility. Primary chip + small "+N"
                        indicator when there are additional entries.
                        Tooltip lists every label so reviewers can
                        confirm without expanding the row. */}
                    {s.eligibility_label ? (
                      <div className="inline-flex items-center gap-1">
                        <Badge
                          data-testid={`eligibility-${s.id}`}
                          className="bg-[#EEF1F6] text-[#3F4756] hover:bg-[#EEF1F6] text-[10px] font-medium"
                          title={
                            (s.eligibility_categories || []).length > 1
                              ? (s.eligibility_categories || [])
                                  .map((e) => `${e.label || e.category}${e.designation === "primary" ? " (primary)" : ""}`)
                                  .join(" · ")
                              : s.eligibility_label
                          }
                        >
                          {s.eligibility_abbr}
                        </Badge>
                        {Array.isArray(s.eligibility_categories) && s.eligibility_categories.length > 1 && (
                          <span
                            data-testid={`eligibility-extra-${s.id}`}
                            className="text-[9px] text-[#6B7280] font-semibold tabular-nums"
                            title="Additional eligibility categories"
                          >
                            +{s.eligibility_categories.length - 1}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-[#B85C5C] italic" title="Eligibility category not set">unset</span>
                    )}
                  </td>
                  <DueCell date={s.iep_due_date} statusKey={s.iep_due_status} testid={`iep-${s.id}`} />
                  <DueCell date={s.met_due_date} statusKey={s.met_due_status} testid={`met-${s.id}`} />
                  <td className="px-2 py-2.5">
                    <div className="max-w-[150px] min-[1920px]:max-w-[220px]">
                    {(() => {
                      // FP3-043 Phase A follow-up #8 — Prefer the
                      // canonical school name resolved via
                      // `school_id` when available. Fall back to the
                      // legacy `campus` text; flag rows whose stale
                      // text doesn't match any school in the org.
                      const linked = s.school_id ? schoolsById[s.school_id] : null;
                      const validNames = new Set(schools.map((x) => (x.name || "").toLowerCase()));
                      const text = (s.campus || "").trim();
                      const stale = !linked && text && schools.length > 0 && !validNames.has(text.toLowerCase());
                      if (linked) {
                        return (
                          <p className="text-[12px] text-[#1F2937] truncate" data-testid={`student-campus-${s.id}`}>
                            {linked.name}
                          </p>
                        );
                      }
                      if (stale) {
                        return (
                          <p
                            className="text-[12px] text-[#9C6B2E] italic"
                            title="This row's campus text doesn't match any school in your organization. Use Schools & Campuses → Repair campus links."
                            data-testid={`student-campus-${s.id}`}
                          >
                            {text} <span className="text-[10px]">· unlinked</span>
                          </p>
                        );
                      }
                      return (
                        <p className="text-[12px] text-[#1F2937] truncate" data-testid={`student-campus-${s.id}`}>
                          {text || "—"}
                        </p>
                      );
                    })()}
                    </div>
                  </td>
                  <td className="px-2 py-2.5">
                    <p className="text-[12px] text-[#3F4756] truncate max-w-[140px]" data-testid={`student-teacher-${s.id}`}>
                      {(s.teacher || "").trim() || deriveTeacherDisplay(s.teacher_email) || "—"}
                    </p>
                  </td>
                  <td className="px-2 py-2.5">
                    <p className="text-[12px] text-[#3F4756] whitespace-nowrap" data-testid={`student-attendance-${s.id}`}>
                      {normalizeAttendancePattern(s.attendance_pattern)
                        ? attendanceLabel(normalizeAttendancePattern(s.attendance_pattern))
                        : "—"}
                    </p>
                  </td>
                  <td className="px-2 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(s.assigned_providers || []).length === 0 ? (
                        <span className="text-[10px] text-[#9B9890] italic">none</span>
                      ) : (
                        s.assigned_providers.map((p) => {
                          // FP3-093B — status-aware provider chips:
                          // ended relationships (graduated/archived)
                          // render muted with an explicit label so
                          // history never masquerades as current.
                          const st = (p.assignment_status || "active").toLowerCase();
                          if (st === "graduated" || st === "archived") {
                            const cls = st === "graduated"
                              ? "bg-[#F5EDDD] border-[#E3CD9C] text-[#8A6B33]"
                              : "bg-[#F2ECEA] border-[#D8BCB6] text-[#8C5F5A]";
                            return (
                              <Badge
                                key={p.id}
                                variant="outline"
                                data-testid={`student-${s.id}-past-provider-${p.id}`}
                                className={`text-[10px] font-normal ${cls}`}
                                title={`${p.name} · ${p.role || ""} — ${st} (historical relationship)`}
                              >
                                {p.name}
                                <span className="ml-1 font-semibold uppercase tracking-wide text-[8.5px]">
                                  · {st}
                                </span>
                              </Badge>
                            );
                          }
                          return (
                            <Badge
                              key={p.id}
                              variant="outline"
                              className="text-[10px] border-[#E5E3DB] text-[#3F4756] font-normal"
                              title={`${p.name} · ${p.role || ""}${st === "monitoring" ? " — monitoring" : ""}`}
                            >
                              {p.name}
                            </Badge>
                          );
                        })
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 hidden 2xl:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {/* FP3-085 — Areas of Impact column. Renders
                          `impact_domains` (broad-domain projection of
                          eligibility subcategories). Same 10-label chip
                          set. Does NOT render `domains` (service
                          domains derived from services[]). */}
                      {(s.impact_domains || []).length === 0 ? (
                        <span className="text-[10px] text-[#9B9890] italic">—</span>
                      ) : (
                        s.impact_domains.map((d) => {
                          const c = DOMAIN_COLORS[d] || { bg: "bg-[#F3F1EC]", fg: "text-[#6B7280]" };
                          return (
                            <Badge
                              key={d}
                              data-testid={`student-impact-${s.id}-${d}`}
                              className={`text-[10px] font-normal ${c.bg} ${c.fg} hover:${c.bg}`}
                              title="From Areas of Impact under Eligibility"
                            >
                              {DOMAIN_LABELS[d] || d}
                            </Badge>
                          );
                        })
                      )}
                    </div>
                  </td>
                  {/* FP3-035B-1 follow-up — LRE chip. Letter-only
                      tag with the full label as tooltip; em dash on
                      unset. */}
                  <td className="px-2 py-2.5 hidden min-[1920px]:table-cell" data-testid={`lre-${s.id}`}>
                    {s.lre ? (
                      <Badge
                        className="bg-[#F1EEF6] text-[#4A3F66] hover:bg-[#F1EEF6] text-[10px] font-semibold tabular-nums"
                        title={s.lre_label || `Level ${s.lre}`}
                      >
                        {s.lre}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-[#9B9890] italic" title="LRE not set">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums hidden min-[1920px]:table-cell">
                    <p className="text-[12px] font-medium text-[#1F2937]">
                      {s.weekly_service_minutes ? `${s.weekly_service_minutes}m` : "—"}
                    </p>
                    {s.service_obligations_count > 0 && (
                      <p className="text-[10px] text-[#6B7280]">{s.service_obligations_count} ob</p>
                    )}
                  </td>
                  <td className="px-2 py-2.5 hidden min-[1920px]:table-cell">
                    <Badge className={`${STATUS_COLORS[s.status] || STATUS_COLORS.active} hover:${STATUS_COLORS[s.status] || STATUS_COLORS.active} text-[10px]`}>
                      {STATUS_LABELS[s.status] || s.status}
                    </Badge>
                    {/* FP3-043 Phase C4 follow-up — Inline exit/archive
                        reason pill for archived rows. Keeps the top
                        "Archived" filter as the single status concept
                        while still surfacing why each student exited.
                        Falls back to legacy `archive_reason` for rows
                        archived before the structured enum existed. */}
                    {s.status === "archived" && (s.exit_reason || s.archive_reason) && (
                      <div
                        data-testid={`student-archive-reason-${s.id}`}
                        className="text-[10px] text-[#6B7280] mt-0.5"
                      >
                        {EXIT_REASON_LABELS[s.exit_reason] ||
                          s.exit_reason ||
                          s.archive_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-[11px] text-[#6B7280] tabular-nums hidden min-[1920px]:table-cell">
                    {s.exit_date ? fmtDate(s.exit_date) : <span className="text-[#D7D5CD]">—</span>}
                  </td>
                  <td className="px-2 py-2.5 text-[11px] text-[#6B7280] tabular-nums hidden min-[1920px]:table-cell">{fmtDate(s.created_at)}</td>
                  {canArchive && (
                    <td
                      className="sticky right-0 z-10 bg-white group-hover:bg-[#FAFAF7] px-2 py-2.5 text-right shadow-[-2px_0_2px_-1px_rgba(0,0,0,0.04)]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {s.status === "archived" || s.is_archived ? (
                        <div
                          data-testid={`student-archived-state-${s.id}`}
                          className="inline-flex items-center gap-1.5"
                          title={
                            (EXIT_REASON_LABELS[s.exit_reason] || s.exit_reason || s.archive_reason || "Archived")
                            + (s.exited_at || s.archived_at ? ` · ${fmtDate(s.exited_at || s.archived_at)}` : "")
                            + (s.exit_note ? ` · ${s.exit_note}` : "")
                          }
                        >
                          <span className="text-[10px] text-[#9AA0A6] tabular-nums whitespace-nowrap">
                            Archived
                            {(s.exited_at || s.archived_at) && (
                              <span className="ml-1 text-[9px]">
                                {fmtDate(s.exited_at || s.archived_at)}
                              </span>
                            )}
                          </span>
                          <Button
                            data-testid={`student-restore-${s.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[11px] text-[#1E5A3F] hover:bg-[#E1EAE4]"
                            onClick={() => {
                              setRestoreTarget({
                                id: s.id,
                                name: s.name || s.first_name || "Student",
                              });
                              setRestoreModalOpen(true);
                            }}
                            title="Restore to active"
                          >
                            <ArrowCounterClockwise size={13} weight="duotone" />
                          </Button>
                          <Button
                            data-testid={`student-permadelete-${s.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[11px] text-[#7A1E1E] hover:bg-[#FBEEEA]"
                            onClick={() => {
                              setPermaDeleteIds([s.id]);
                              setPermaDeleteOpen(true);
                            }}
                            title="Permanently delete (pre-service only)"
                          >
                            <Trash size={13} weight="duotone" />
                          </Button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center">
                          <Button
                            data-testid={`student-archive-${s.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[11px] text-[#7A3A2C] hover:bg-[#F8EFEA]"
                            onClick={() => {
                              setArchiveTargetIds([s.id]);
                              setArchiveModalOpen(true);
                            }}
                            title="Archive / Exit student"
                          >
                            <Archive size={13} weight="duotone" />
                          </Button>
                          {/* FP3-088 — Permanent Delete row action,
                              distinct from Archive. */}
                          <Button
                            data-testid={`student-permadelete-${s.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[11px] text-[#7A1E1E] hover:bg-[#FBEEEA]"
                            onClick={() => {
                              setPermaDeleteIds([s.id]);
                              setPermaDeleteOpen(true);
                            }}
                            title="Permanently delete (pre-service only)"
                          >
                            <Trash size={13} weight="duotone" />
                          </Button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* FP3-043 Phase C4 — Single + bulk archive/exit modal. */}
      <ArchiveStudentsModal
        open={archiveModalOpen}
        onOpenChange={setArchiveModalOpen}
        studentIds={archiveTargetIds}
        onArchived={() => {
          setSelectedIds(new Set());
          setReloadToken((k) => k + 1);
        }}
      />

      {/* FP3-051 — Restore archived student to active. */}
      <RestoreStudentModal
        open={restoreModalOpen}
        onOpenChange={setRestoreModalOpen}
        studentId={restoreTarget.id}
        studentName={restoreTarget.name}
        onRestored={() => setReloadToken((k) => k + 1)}
      />

      {/* FP3-088 — Permanent Delete (server-side preflight, typed
          confirmation, blocked students never deleted). */}
      <PermanentDeleteModal
        open={permaDeleteOpen}
        onOpenChange={setPermaDeleteOpen}
        studentIds={permaDeleteIds}
        contextParams={permaCtx}
        onDeleted={() => {
          setSelectedIds(new Set());
          setPermaDeleteIds([]);
          setReloadToken((k) => k + 1);
        }}
      />

      {/* FP3-077 — Bulk service-domain / provider-assignment cleanup. */}
      <BulkActionsModal
        open={!!bulkKind}
        onClose={closeBulk}
        kind={bulkKind}
        studentIds={Array.from(selectedIds)}
        students={visible.filter((s) => selectedIds.has(s.id))}
        providers={adminProviders}
        onApplied={() => {
          setSelectedIds(new Set());
          setReloadToken((k) => k + 1);
        }}
      />

      {/* FP3-035B-1 — Manual Add Student modal. Triggered by the
          header button; refresh-token forces the listing to reload
          after a successful create. */}
      {addModalOpen && (
        <AdminAddStudentModal
          onClose={() => setAddModalOpen(false)}
          onCreated={() => {
            setAddModalOpen(false);
            setReloadToken((n) => n + 1);
          }}
          // FP3-043 Phase A follow-up #7 — Modal now fetches its own
          // org-scoped schools list via /api/schools and no longer
          // accepts a campus name prop. We still pass the deprecated
          // `campuses` so legacy build paths keep compiling, but the
          // modal ignores it.
          campuses={summary?.campuses || []}
        />
      )}
    </AdminShell>
  );
}


function SortHeader({ testid, label, active, dir, onClick, align = "left", sticky = false, stickyOffset = "left-0", className = "" }) {
  return (
    <th
      data-testid={testid}
      onClick={onClick}
      className={`px-2 py-2 text-[10px] uppercase tracking-wider font-semibold cursor-pointer hover:text-[#1F2937] select-none ${
        align === "right" ? "text-right" : "text-left"
      } ${
        // FP3-035B-1 — Sticky Student column keeps the row identity
        // visible while horizontally scrolling. `bg-[#F9F8F6]` matches
        // the thead so the sticky cell hides the row beneath.
        // FP3-043 Phase C4 — stickyOffset shifts the column when the
        // bulk-select checkbox column is present.
        sticky
          ? `sticky ${stickyOffset} z-20 bg-[#F9F8F6] shadow-[2px_0_2px_-1px_rgba(0,0,0,0.04)]`
          : ""
      } ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </span>
    </th>
  );
}

// Iter 34 — Due-date cell with status-driven colorization. Overdue → red,
// soon (≤30d) → amber, ok → neutral, unset → soft gray italic.
function DueCell({ date, statusKey, testid, className = "" }) {
  if (!date) {
    return (
      <td data-testid={testid} className={`px-2 py-2.5 ${className}`}>
        <span className="text-[10px] text-[#9B9890] italic">unset</span>
      </td>
    );
  }
  const tone =
    statusKey === "overdue" ? { bg: "bg-[#FBEFEC]", fg: "text-[#B85C5C]", note: "overdue" }
    : statusKey === "soon" ? { bg: "bg-[#FDF8F3]", fg: "text-[#9C6B2E]", note: "≤30d" }
    : { bg: "", fg: "text-[#1F2937]", note: "" };
  return (
    <td data-testid={testid} className={`px-2 py-2.5 tabular-nums ${className}`}>
      <div className="flex flex-col">
        <span className={`text-[12px] font-medium ${tone.fg} ${tone.bg ? `${tone.bg} px-1.5 py-0.5 rounded inline-block self-start` : ""}`}>
          {fmtDate(date)}
        </span>
        {tone.note && <span className="text-[9px] uppercase tracking-wider text-[#6B7280] mt-0.5">{tone.note}</span>}
      </div>
    </td>
  );
}



// FP3-043 Phase A follow-up #8 — Self-serve campus-link repair UI.
// Renders an org-admin–safe action that runs a dry-run against the
// admin's own org first, surfaces the counts, and lets the admin
// commit. Cross-org movement is impossible (the endpoint enforces
// org scope on the backend regardless of params).
function CampusLinkHygiene({ count, onAfterApply }) {
  const [open, setOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const runAudit = async () => {
    setError("");
    setAudit(null);
    setOpen(true);
    try {
      const r = await api.campusLinkRepairAudit();
      setAudit(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Audit failed");
    }
  };
  const apply = async () => {
    setApplying(true);
    setError("");
    try {
      const r = await api.campusLinkRepairApply();
      setAudit(r.data);
      onAfterApply?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Apply failed");
    } finally {
      setApplying(false);
    }
  };
  return (
    <div
      data-testid="campus-link-hygiene-strip"
      className="mb-3 flex items-start gap-2 px-3 py-2 rounded-md border border-[#9C6B2E]/25 bg-[#FDF8F3]/60 text-[11px] text-[#9C6B2E] flex-wrap"
    >
      <Buildings size={12} weight="duotone" className="mt-0.5" />
      <div className="flex-1 min-w-[260px]">
        <p className="font-medium">
          Campus links:{" "}
          <span className="font-semibold tabular-nums" data-testid="campus-link-stale-count">
            {count}
          </span>{" "}
          student{count === 1 ? "" : "s"} with a campus name that doesn't match any school in your org.
        </p>
        {open && audit && (
          <div className="mt-1.5 text-[10.5px] text-[#6B5B3A] space-y-0.5">
            <p>
              <span className="font-semibold">scanned</span>: {audit?.counts?.scanned ?? 0}{" "}·{" "}
              <span className="font-semibold">eligible</span>: {audit?.counts?.eligible ?? 0}{" "}·{" "}
              <span className="font-semibold">matched</span>: {audit?.counts?.matched_unambiguous ?? 0}
            </p>
            <p>
              <span className="font-semibold">skipped (no match)</span>: {audit?.counts?.skipped_zero_match ?? 0}{" "}·{" "}
              <span className="font-semibold">skipped (multiple)</span>: {audit?.counts?.skipped_multiple_match ?? 0}
            </p>
            {audit?.apply && (
              <p data-testid="campus-link-applied-line">
                <span className="font-semibold">updated</span>: {audit?.counts?.updated ?? 0} (idempotent — safe to re-run)
              </p>
            )}
          </div>
        )}
        {error && (
          <p className="mt-1 text-[10.5px] text-[#B85C5C]" data-testid="campus-link-error">
            {error}
          </p>
        )}
      </div>
      {!open ? (
        <button
          type="button"
          onClick={runAudit}
          data-testid="campus-link-audit-btn"
          className="px-2.5 py-1 rounded-md text-[10.5px] font-medium bg-[#9C6B2E] text-white hover:bg-[#7c5524]"
        >
          Repair campus links…
        </button>
      ) : audit && audit.counts?.matched_unambiguous > 0 && !audit.apply ? (
        <button
          type="button"
          onClick={apply}
          disabled={applying}
          data-testid="campus-link-apply-btn"
          className="px-2.5 py-1 rounded-md text-[10.5px] font-medium bg-[#9C6B2E] text-white hover:bg-[#7c5524] disabled:opacity-60"
        >
          {applying ? "Repairing…" : `Repair ${audit.counts.matched_unambiguous} link${audit.counts.matched_unambiguous === 1 ? "" : "s"}`}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="campus-link-close-btn"
          className="px-2.5 py-1 rounded-md text-[10.5px] font-medium bg-transparent text-[#6B5B3A] hover:bg-[#F3EAD8]"
        >
          Close
        </button>
      )}
    </div>
  );
}
